import React, { useMemo } from 'react';
import { Users, Target, BadgeCheck, Briefcase, ArrowUpRight, ArrowDownRight } from 'lucide-react';

interface SimpleBarChartProps {
  data: number[];
  labels: string[];
  color?: string;
}

const SimpleBarChart = ({ data, labels, color = 'bg-brand-primary' }: SimpleBarChartProps) => {
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end space-x-2 h-40">
      {data.map((val, i) => (
        <div key={i} className="flex-1 flex flex-col items-center space-y-1">
          <div className="w-full relative" style={{ height: `${(val / max) * 100}%`, minHeight: '4px' }}>
            <div className={`absolute bottom-0 left-0 right-0 rounded-t-md ${color} opacity-80 hover:opacity-100 transition`} style={{ height: '100%' }} />
          </div>
          <span className="text-[11px] text-slate-500">{labels[i]}</span>
          <span className="text-[11px] font-semibold text-slate-700">{val}</span>
        </div>
      ))}
    </div>
  );
};

interface Props {
  candidates: any[];
  /** REF-20: the empty state's next action (opens Add Candidate). */
  onReferFirstCandidate?: () => void;
}

export default function ReferrerPerformanceView({ candidates, onReferFirstCandidate }: Props) {
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const filteredCandidates = useMemo(() => {
    return candidates.filter((c: any) => {
      if (!c.createdAt) return true;
      const date = new Date(c.createdAt);
      if (startDate) {
        const start = new Date(startDate + 'T00:00:00');
        if (date < start) return false;
      }
      if (endDate) {
        const end = new Date(endDate + 'T23:59:59');
        if (date > end) return false;
      }
      return true;
    });
  }, [candidates, startDate, endDate]);

  const perfMetrics = useMemo(() => {
    const total = filteredCandidates.length;
    const placed = filteredCandidates.filter((c: any) => {
      const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
      return ms >= 4;
    }).length;
    const visaApproved = filteredCandidates.filter((c: any) => {
      const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
      return ms >= 7;
    }).length;
    const conversionRate = total > 0 ? Math.round((placed / total) * 100) : 0;
    const visaRate = placed > 0 ? Math.round((visaApproved / placed) * 100) : 0;

    const months: { labels: string[]; applications: number[]; placements: number[]; interviews: number[]; offers: number[]; hires: number[] } = {
      labels: [], applications: [], placements: [], interviews: [], offers: [], hires: [],
    };
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.labels.push(d.toLocaleString('default', { month: 'short' }));
      const monthCands = filteredCandidates.filter((c: any) => {
        const created = c.createdAt ? new Date(c.createdAt) : null;
        return created && created.getMonth() === d.getMonth() && created.getFullYear() === d.getFullYear();
      });
      months.applications.push(monthCands.length);
      months.placements.push(monthCands.filter((c: any) => {
        const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
        return ms >= 4;
      }).length);
      months.interviews.push(monthCands.filter((c: any) => {
        const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
        return ms >= 2 && ms <= 3;
      }).length);
      months.offers.push(monthCands.filter((c: any) => {
        const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
        return ms >= 5;
      }).length);
      months.hires.push(monthCands.filter((c: any) => {
        const ms = typeof c.masterStage === 'number' ? c.masterStage : parseInt(c.masterStage || '0');
        return ms >= 4;
      }).length);
    }

    return { totalCandidates: total, placed, visaApproved, conversionRate, visaRate, ...months };
  }, [filteredCandidates]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Performance Dashboard</h1>
        <p className="text-sm text-slate-500">{candidates.length === 0
          ? 'Track your referral metrics and progress'
          : <>Track your referral metrics based on {perfMetrics.totalCandidates} referred candidate{perfMetrics.totalCandidates !== 1 ? 's' : ''}</>}</p>
      </div>

      {candidates.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-12 text-center border border-slate-200">
          <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No performance data yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto leading-relaxed">
            Once you refer your first candidate, your referral metrics and monthly charts will appear here.
          </p>
          {onReferFirstCandidate && (
            <button
              type="button"
              onClick={onReferFirstCandidate}
              className="mt-5 px-4 py-2.5 btn-primary text-white rounded-xl text-sm font-semibold transition cursor-pointer"
            >
              Refer your first candidate
            </button>
          )}
        </div>
      ) : (
      <>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center gap-3">
        <span className="text-xs font-bold text-slate-700 shrink-0">Filter Date Range:</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            aria-label="Start date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
          />
          <span className="text-xs text-slate-500">to</span>
          <input
            type="date"
            aria-label="End date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-2.5 py-3 md:py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-base md:text-xs font-bold outline-none focus:bg-white"
          />
        </div>
        {(startDate || endDate) && (
          <button
            onClick={() => { setStartDate(''); setEndDate(''); }}
            className="text-xs text-red-500 font-bold hover:underline cursor-pointer ml-auto"
          >
            Clear Date Filter
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Referred', value: perfMetrics.totalCandidates, icon: Users, trend: 'up' },
          { label: 'Placement Rate', value: `${perfMetrics.conversionRate}%`, icon: Target, trend: perfMetrics.conversionRate >= 50 ? 'up' : 'down' },
          { label: 'Visa Success Rate', value: `${perfMetrics.visaRate}%`, icon: BadgeCheck, trend: perfMetrics.visaRate >= 70 ? 'up' : 'down' },
          { label: 'Placed Candidates', value: perfMetrics.placed, icon: Briefcase, trend: perfMetrics.placed > 0 ? 'up' : 'down' },
        ].map((m, i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm ring-1 ring-slate-100">
            <div className="flex items-center justify-between mb-2">
              <m.icon className="h-4 w-4 text-slate-400" />
              {m.trend === 'up' ? <ArrowUpRight className="h-3 w-3 text-emerald-500" /> : <ArrowDownRight className="h-3 w-3 text-red-500" />}
            </div>
            <p className="text-lg font-bold text-slate-900">{m.value}</p>
            <p className="text-[11px] text-slate-500 font-medium">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Monthly Applications</h3>
          <SimpleBarChart data={perfMetrics.applications} labels={perfMetrics.labels} color="bg-brand-primary" />
        </div>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Monthly Placements</h3>
          <SimpleBarChart data={perfMetrics.placements} labels={perfMetrics.labels} color="bg-emerald-500" />
        </div>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Interviews Scheduled</h3>
          <SimpleBarChart data={perfMetrics.interviews} labels={perfMetrics.labels} color="bg-amber-500" />
        </div>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 p-5">
          <h3 className="font-semibold text-slate-900 mb-4">Offers & Hires</h3>
          <div className="flex items-end space-x-2 h-40">
            {perfMetrics.offers.map((val: number, i: number) => {
              const max = Math.max(...perfMetrics.offers, ...perfMetrics.hires, 1);
              return (
                <div key={i} className="flex-1 flex flex-col items-center space-y-1">
                  <div className="w-full relative" style={{ height: `${(val / max) * 100}%`, minHeight: '4px' }}>
                    <div className="absolute bottom-0 left-0 right-0 rounded-t-md bg-blue-500 opacity-80" style={{ height: '100%' }} />
                  </div>
                  <div className="w-full relative" style={{ height: `${(perfMetrics.hires[i] / max) * 100}%`, minHeight: '4px' }}>
                    <div className="absolute bottom-0 left-0 right-0 rounded-t-md bg-emerald-500 opacity-80" style={{ height: '100%' }} />
                  </div>
                  <span className="text-[11px] text-slate-500">{perfMetrics.labels[i]}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center space-x-4 mt-3">
            <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-blue-500 rounded-sm" /><span className="text-[11px] text-slate-500">Offers</span></div>
            <div className="flex items-center space-x-1"><div className="w-3 h-3 bg-emerald-500 rounded-sm" /><span className="text-[11px] text-slate-500">Hires</span></div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
